import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PlusCircle, MinusCircle, ArrowRight, Check } from 'lucide-react';
import { useSettings, formatTodayLong } from '../lib/settings';
import { useDailyHabits } from '../lib/habitState';
import { useProjectTime, formatDuration } from '../lib/projectTime';
import { loadEntry, saveEntry } from '../lib/journal';
function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function SelfReflection() {
  const navigate = useNavigate();
  const settings = useSettings();
  const {
    completedCount,
    totalCount
  } = useDailyHabits();
  const {
    minutesLogged
  } = useProjectTime();
  const hpPercentage = totalCount === 0 ? 0 : Math.round(completedCount / totalCount * 100);
  const goalMinutes = settings.ppHours * 60 + settings.ppMinutes;
  const ppPercentage = goalMinutes === 0 ? 0 : Math.round(minutesLogged / goalMinutes * 100);
  // Reflection form state — load any previously saved values for today
  const [wins, setWins] = useState<[string, string, string]>(['', '', '']);
  const [blocker, setBlocker] = useState('');
  const [tomorrowsTweak, setTomorrowsTweak] = useState('');
  const [justSaved, setJustSaved] = useState(false);
  useEffect(() => {
    const existing = loadEntry(todayKey());
    if (existing) {
      if (existing.wins) setWins(existing.wins);
      if (existing.blocker) setBlocker(existing.blocker);
      if (existing.tomorrowsTweak) setTomorrowsTweak(existing.tomorrowsTweak);
    }
  }, []);
  const updateWin = (idx: 0 | 1 | 2, value: string) => {
    setWins(prev => {
      const next = [...prev] as [string, string, string];
      next[idx] = value;
      return next;
    });
  };
  const handleComplete = () => {
    saveEntry(todayKey(), {
      hpCompleted: completedCount,
      hpTotal: totalCount,
      ppCompleted: minutesLogged,
      ppTotal: goalMinutes,
      wins,
      blocker,
      tomorrowsTweak
    });
    setJustSaved(true);
    setTimeout(() => {
      navigate('/');
    }, 600);
  };
  return <div className="max-w-2xl mx-auto p-8 pb-24" data-id="element-250">
      {/* Header */}
      <div className="flex items-center justify-between mb-8" data-id="element-251">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-252">
          <ArrowLeft className="w-5 h-5" data-id="element-253" />
        </button>
        <span className="text-sm font-medium text-gray-500" data-id="element-254">
          {formatTodayLong()}
        </span>
        <span className="w-16" data-id="element-255" />
      </div>

      <div className="space-y-6" data-id="element-256">
        {/* Goal & Score Card */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 text-center" data-id="element-257">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2" data-id="element-258">
            Goal
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-8" data-id="element-259">
            {settings.goal || 'No goal set — add one in Settings'}
          </h3>

          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-6" data-id="element-260">
            Today's Progress
          </div>

          <div className="space-y-4 mb-6" data-id="element-261">
            {/* HP — fills right-to-left */}
            <div className="flex items-center gap-4" data-id="element-262">
              <span className="text-xs font-bold text-gray-900 w-6" data-id="element-263">HP</span>
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden flex justify-end" data-id="element-264">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-500 ease-out" style={{
                width: `${hpPercentage}%`
              }} data-id="element-265" />
              </div>
              <span className="text-xs font-medium text-gray-500 w-10 text-right" data-id="element-266">
                {hpPercentage}%
              </span>
            </div>
            {/* PP — fills right-to-left */}
            <div className="flex items-center gap-4" data-id="element-267">
              <span className="text-xs font-bold text-gray-900 w-6" data-id="element-268">PP</span>
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden flex justify-end" data-id="element-269">
                <div className="h-full bg-indigo-500 rounded-full transition-all duration-500 ease-out" style={{
                width: `${Math.min(ppPercentage, 100)}%`
              }} data-id="element-270" />
              </div>
              <span className="text-xs font-medium text-gray-500 w-10 text-right" data-id="element-271">
                {ppPercentage}%
              </span>
            </div>
          </div>

          <div className="text-xs text-gray-400 font-medium text-left" data-id="element-272">
            {completedCount}/{totalCount} habits •{' '}
            {formatDuration(minutesLogged)} / {formatDuration(goalMinutes)} deep
            work
          </div>
        </div>

        {/* Reflection Sections */}
        <div className="space-y-8 pt-4" data-id="element-273">
          {/* Wins */}
          <div className="relative pl-6" data-id="element-274">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500 rounded-full" data-id="element-275" />
            <div className="flex items-center gap-2 mb-4" data-id="element-276">
              <PlusCircle className="w-5 h-5 text-emerald-500" data-id="element-277" />
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-278">
                What Went Well (3 Wins)
              </h3>
            </div>
            <div className="space-y-3" data-id="element-279">
              {([0, 1, 2] as const).map(idx => <input key={idx} type="text" value={wins[idx]} onChange={e => updateWin(idx, e.target.value)} placeholder={['1. Accomplished X...', '2. Had a great meeting with...', '3. Finished the design for...'][idx]} className="w-full p-3.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none shadow-sm" data-id="element-280" />)}
            </div>
          </div>

          {/* Blockers */}
          <div className="relative pl-6" data-id="element-281">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-rose-500 rounded-full" data-id="element-282" />
            <div className="flex items-center gap-2 mb-4" data-id="element-283">
              <MinusCircle className="w-5 h-5 text-rose-500" data-id="element-284" />
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-285">
                What held me back today?
              </h3>
            </div>
            <div className="space-y-2" data-id="element-286">
              <input type="text" value={blocker} onChange={e => setBlocker(e.target.value)} placeholder="Be specific — low energy after lunch, distr..." className="w-full p-3.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-rose-500 focus:border-transparent outline-none shadow-sm" data-id="element-287" />
              <p className="text-xs text-gray-400 ml-1" data-id="element-288">
                Keep it brief and honest
              </p>
            </div>
          </div>

          {/* Tomorrow's Tweak */}
          <div className="relative pl-6 bg-indigo-50/50 rounded-r-2xl py-6 pr-6 -mr-6" data-id="element-289">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-500 rounded-full" data-id="element-290" />
            <div className="flex items-center gap-2 mb-4" data-id="element-291">
              <ArrowRight className="w-5 h-5 text-indigo-500" data-id="element-292" />
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-293">
                Tomorrow's Tweak
              </h3>
            </div>
            <input type="text" value={tomorrowsTweak} onChange={e => setTomorrowsTweak(e.target.value)} placeholder='Keep it small and actionable (e.g. "Start ...' className="w-full p-3.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none shadow-sm" data-id="element-294" />
          </div>
        </div>

        {/* Complete Button */}
        <button onClick={handleComplete} className="w-full py-4 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold rounded-xl shadow-sm transition-colors mt-8 flex items-center justify-center gap-2" data-id="element-295">
          {justSaved ? <>
              <Check className="w-4 h-4" data-id="element-296" />
              Saved
            </> : "Complete Today's GPS"}
        </button>
      </div>
    </div>;
}